// The JSP screens. HTML only. Ported card by card from MOCKUP_JSP_APPROVED.html rev 8 (approved 2026-10-08).
// Each node keeps the mockup's words; task lines come from the live task content (jsp-content-data.ts).
// Buttons carry data-j* attributes so jsp-page.ts owns every event.

import {
  BINDING_HANDLE_LINE,
  cancelConfirmText,
  ENERGY_OPTIONS,
  ESCORT_LINE,
  EXPOSE_LINES,
  LIVE_PARTS_LINE,
  PROMPT_ANSWER_LABEL,
  RAB_LINE,
  WORK_AREA_QUESTION,
  asksWorkArea,
  doorPlane,
  isBad,
  liveRemains,
  LOCK_STEPS,
  NEED_POWER_REASONS,
  REENERGIZE_CHECKS,
  NODE_SUB,
  RAISE_REASONS,
  TIER_LABEL,
  condDone,
  doneLine,
  energyList,
  energyOk,
  escalPending,
  findings,
  glovesAnswered,
  isDead,
  ldlStepsOf,
  lockOutDone,
  promptsDone,
  promptsFor,
  railOf,
  scopeDeenPrompt,
  sourceCountText,
  tier1Gloves,
  verdictOf,
  type JspAsset,
  type JspPrompt,
  type JspState,
  type JspTask,
} from "./jsp";
import {
  JUSTIFY_OPTIONS,
  NFPA_REASONS,
  NOT_REASONS,
  NO_APPROVERS,
  SAFE_NO,
  approvedCount,
  clockOf,
  coverNeedsPermit,
  eewpAnswersFrom,
  eewpAnswersProblem,
  eewpLinkAt,
  jspHalfOf,
  linkWords,
  permitIdOf,
  restrictedApproachOf,
  scopeOf,
  untilLine,
  waitingOn,
  type EewpAnswers,
  type EewpContext,
  type EewpFrom,
  type EewpSummary,
  type PermitView,
  STANDBY_TICKS,
} from "./eewp";
import { GLOVE_DEFECTS } from "./gloves";
import { LEFT_AS_LABEL, REPAIR_NOTE_PROMPT } from "./jsp-close";
import { LEVEL_RANK, type Level, type Verdict } from "./risk-verdict";

export interface JspPill {
  label: string;
  tone: "green" | "amber" | "red" | "grey";
}

export interface JspView {
  a: JspAsset;
  t: JspTask;
  s: JspState;
  jspNumber: string;
  pills: JspPill[];
  /** The close pill, after Submit. */
  close: { open: boolean; work: string | null; leftAs: string | null; note: string; isolation: string | null; reEnergize: Record<string, boolean>; busy: boolean; done: boolean; error: string; autoAt?: string | null };
  busy: boolean;
  error: string;
  /** EEWP, QEW side (EEWP_WORDS.md rev 2). None when the page has no EEWP context. */
  ew?: EewpView;
  /** Cancel before Submit (MOCKUP_JSP_CANCEL.html, approved 2026-10-14). */
  cancel?: { ask: boolean; busy: boolean; error: string; taskName: string; startedTime: string };
}

/** The EEWP screens open in place of the active node, Before You Ask first. */
export interface EewpFlow {
  step: "ask1" | "ask2" | "form" | "review";
  from: EewpFrom;
  answers: EewpAnswers;
  /** The optional photo of the work area, as a small JPEG data URL. */
  photo: string | null;
}

export interface EewpView {
  ctx: EewpContext | null;
  flow: EewpFlow | null;
  permit: EewpSummary | null;
  /** Resume: reading the permit before the node shows. */
  checking: boolean;
  /** The site's Active QEWs, other than this one. Loaded when the flow opens. */
  qews: { badgeId: string; name: string }[];
  qewsLoaded: boolean;
  /** The asset's location, for the frozen half. */
  location: string;
  /** MM/DD, for a glove inspection on this JSP. */
  todayShort: string;
  /** The whole permit for Print, without emails or link codes. */
  print: PermitView | null;
  /** Confirm PPE And Begin: the items ticked so far. */
  ppeTicks: Record<string, boolean>;
  busy: boolean;
  error: string;
}

export function noEewpView(): EewpView {
  return { ctx: null, flow: null, permit: null, checking: false, qews: [], qewsLoaded: false, location: "", todayShort: "", print: null, ppeTicks: {}, busy: false, error: "" };
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
/** Title case for node titles (JSP_ADJUSTMENTS item 28). */
function tc(x: string): string {
  return x.replace(/(^|[\s(/-])([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase());
}
function head(n: number, tot: number, title: string, sub = ""): string {
  return `<div class="card"><div class="nn">Node ${n} of ${tot}</div><div class="tt">${esc(tc(title))}</div>${sub ? `<div class="tsub">${sub}</div>` : ""}`;
}
const sec = (t: string) => `<div class="sec">${t}</div>`;
const tile = (h: string, l: Level) => `<div class="tile L-${l}"><div class="h">${h}</div><div class="v">${l}</div></div>`;
const gold = (l: string, a: string) => `<button type="button" class="gbtn" data-j="${a}">${l}</button>`;
const off = (l: string) => `<button type="button" class="gbtn off" disabled>${l}</button>`;
function cf(s: JspState, key: string, label: string, sub = ""): string {
  const on = Boolean(s.c[key]);
  return `<button type="button" class="cf${on ? " on" : ""}" data-jcf="${key}"><span class="bx" aria-hidden="true">${on ? "✓" : ""}</span><span><b>${label}</b>${sub ? `<span class="sb">${sub}</span>` : ""}</span></button>`;
}
const all = (s: JspState, keys: string[]) => keys.every((k) => s.c[k]);
function yn(s: JspState, field: keyof JspState["cond"], yes = "Yes", no = "No"): string {
  const cur = s.cond[field];
  return `<div class="opts"><button type="button" class="o" data-jcd="${field}" data-v="0" aria-pressed="${cur === false}">${no}</button><button type="button" class="o" data-jcd="${field}" data-v="1" aria-pressed="${cur === true}">${yes}</button></div>`;
}

function ieText(a: JspAsset): string {
  return a.incidentEnergy === "pending" ? "IE pending study" : a.incidentEnergy === "outsideStudy" ? "Outside the arc flash study" : `${a.incidentEnergy} cal/cm²`;
}
function arcRow(a: JspAsset, atDoorPlane = false): string {
  // Decision 62 item 4: LOW at any IE at the door plane. The row still shows the IE, or IE pending study.
  if (atDoorPlane && a.incidentEnergy === "pending") return "IE pending study. No arc-rated PPE at the door plane. Arc flash still possible. Take precautions.";
  if (atDoorPlane && typeof a.incidentEnergy === "number" && a.incidentEnergy >= 1.2) return `${a.incidentEnergy} cal. No arc-rated PPE at the door plane. Arc flash still possible. Take precautions.`;
  if (a.incidentEnergy === "pending") return `IE pending study. Top stocked PPE: Group 1 (${a.ieCeilingCal} cal).`;
  if (a.incidentEnergy === "outsideStudy") return "Outside the arc flash study by design. Arc flash still possible. Take precautions.";
  if (a.incidentEnergy < 1.2) return `${a.incidentEnergy} cal. Below 1.2. Arc flash still possible. Take precautions.`;
  return `${a.incidentEnergy} cal. Group 1 (${a.ieCeilingCal} cal) arc-rated PPE.`;
}
function shockRow(a: JspAsset): string {
  if (a.volts === null) return "Voltage not on record. Treat as live.";
  if (a.volts < 50) return "Under 50 V.";
  return `${a.volts} V. Class 0 gloves and leather protectors before you reach in. Avoid contact with live parts.`;
}
/** " · RAB 1 ft 0 in" for the boundary tick, or nothing when there is no RAB to show (JSP cleanup PR 1, F3). */
function ragText(a: JspAsset): string {
  if (a.volts === null || a.volts < 50) return "";
  return ` · RAB ${esc(restrictedApproachOf(a.volts))}`;
}
/** The Field Assessment hands question reads the asset's own restricted approach boundary (JSP cleanup PR 1, F1). */
export function nearQuestion(a: JspAsset): { q: string; hlp: string } {
  if (a.volts === null) return { q: "Will your hands or tools come within 1 ft 0 in of exposed live parts?", hlp: "Voltage not on record. Treat it as live." };
  if (a.volts < 50) return { q: "Will your hands or tools touch exposed live parts?", hlp: "" };
  const rab = restrictedApproachOf(a.volts);
  if (a.volts <= 150) return { q: "Will your hands or tools touch exposed live parts?", hlp: `Restricted approach for ${a.volts} V is avoid contact.` };
  if (/^\d/.test(rab)) return { q: `Will your hands or tools come within ${rab} of exposed live parts?`, hlp: `Restricted approach for ${a.volts} V.` };
  return { q: "Will your hands or tools cross the restricted approach boundary?", hlp: rab };
}
function ppeBox(a: JspAsset, atDoorPlane = false): string {
  return `<dl class="rec"><dt>Arc flash</dt><dd>${esc(arcRow(a, atDoorPlane))}</dd><dt>Shock</dt><dd>${esc(shockRow(a))}</dd></dl>`;
}
function posterRow(s: JspState): string {
  const pv = Boolean(s.c.poster);
  return `<button type="button" class="cf${pv ? " on" : ""}" data-j="poster"><span class="bx" aria-hidden="true">${pv ? "✓" : ""}</span><span><b>PPE poster checked for this job</b><span class="sb">${pv ? "Checked. Tap to view again." : "Tap to open the poster."}</span></span></button>`;
}
function gloves(s: JspState): string {
  const g = s.gloves;
  let h = `<div class="q2" style="margin-top:4px">Gloves inspected before use?</div><div class="hlp">Class 0 rubber gloves and leather protectors. A Pass on a pair’s first use puts it in service.</div><div class="opts"><button type="button" class="o" data-jgl="pass" aria-pressed="${g === "pass"}">Pass</button><button type="button" class="o" data-jgl="fail" aria-pressed="${g === "fail"}">Fail</button></div>`;
  if (g === "fail") {
    // What Did You Find? (Robert, 2026-10-09): pick from the list, Other opens one line. JSP_WRITES_WORDS.md section 1.
    const opts = GLOVE_DEFECTS.map((d) => `<option value="${esc(d)}"${s.gloveDefect === d ? " selected" : ""}>${esc(d)}</option>`).join("");
    h += `<label class="gdl" for="jgdef">What Did You Find?</label><select id="jgdef" class="gdsel"><option value=""${s.gloveDefect ? "" : " selected"}>Pick one</option>${opts}</select>`;
    if (s.gloveDefect === "Other") h += `<input id="jgnote" class="gdin" type="text" maxlength="300" aria-label="What you found" placeholder="Tell us what you found" value="${esc(s.gloveDefectNote)}">`;
    h += '<div class="stopm">This pair is out of service. Use another tested pair. Inspect it before you use it.</div>';
  }
  return `${h}<div style="height:8px"></div>`;
}
function ppeLevel(a: JspAsset): string {
  return a.incidentEnergy === "pending" ? `Top stocked PPE: Group 1 (${a.ieCeilingCal} cal)` : `Group 1 (${a.ieCeilingCal} cal)`;
}
function promptBlock(v: JspView, label: string, at: JspPrompt["at"] = "place"): string {
  const ps = promptsFor(v.t, v.s, at);
  if (!ps.length) return "";
  let h = `<div class="pbox"><div class="plbl">${label}</div>`;
  ps.forEach((p, i) => {
    const ans = v.s.prompts[p.k];
    const bad = isBad(p, ans);
    h += `<div class="pq${i ? " sep" : ""}"><div class="q2">${esc(p.q)}</div><div class="opts">${p.opts
      .map((o) => `<button type="button" class="o sm" data-jpr="${esc(p.k)}" data-v="${o}" aria-pressed="${ans === o}">${PROMPT_ANSWER_LABEL[o]}</button>`)
      .join("")}</div>`;
    if (bad) {
      h += `<div class="pwhy">${esc(p.why)}</div>`;
      if (p.deen)
        h += v.s.escalAck[p.k]
          ? '<div class="pack">✓ De-energized steps added to this JSP</div>'
          : `<button type="button" class="gbtn dng" data-jdeen="${esc(p.k)}" style="margin-top:6px;min-height:40px">De-Energize This Job</button>`;
      else if (p.escal) h += v.s.escalAck[p.k] ? '<div class="pack">✓ Acknowledged and logged</div>' : `<button type="button" class="rbtn sm" data-jea="${esc(p.k)}">Acknowledge</button>`;
    }
    h += "</div>";
  });
  return `${h}</div>`;
}
function closeBlock(s: JspState): string {
  if (!s.closing) return '<button type="button" class="closew" data-j="close">Close JSP Without Isolation</button>';
  return '<div class="closebox"><p>Closing records that you planned no isolation.</p><div class="row2"><button type="button" class="rbtn" data-j="back">Go Back</button><button type="button" class="gbtn dng" data-j="closenoiso" style="margin-top:0;min-height:44px">Close JSP</button></div></div>';
}

/** The quiet EEWP line on a node, or nothing. A Short site shows NO_APPROVERS in its place. */
function ewLink(v: JspView, node: string): string {
  const from = eewpLinkAt(node, v.a, v.t, v.s, v.ew!.ctx);
  if (!from || !v.ew!.ctx) return "";
  const p = v.ew!.permit;
  if (p && p.status !== "Waiting" && p.status !== "Approved") return `<div class="ewnone">${esc(p.permitId)} ${esc(p.status.toLowerCase())}. One JSP, one permit. Discard and start a new JSP to ask again.</div>`;
  if (p) return "";
  if (v.ew!.ctx.short) return `<div class="ewnone">${esc(NO_APPROVERS)}</div>`;
  const [lead, link] = linkWords(from, v.ew!.ctx.coverRequired);
  return `<div class="ewq">${esc(lead)} <button type="button" class="ewlk" data-jew="${from}">${esc(link)}</button></div>`;
}

// ---- LDL rehearsal rig, ported from live node-jsp.js (S128b) through the approved mockup ----

interface RigStep {
  type: "verify" | "walk";
  phase: string;
  label: string;
  a?: string;
  b?: string;
}
const LSEQ3: RigStep[] = [
  { type: "verify", phase: "Phase 1 · Meter integrity", label: "Meter check · known live source" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L1 → Ground", a: "L1", b: "G" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L2 → Ground", a: "L2", b: "G" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L3 → Ground", a: "L3", b: "G" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L1 → L2", a: "L1", b: "L2" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L1 → L3", a: "L1", b: "L3" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "L2 → L3", a: "L2", b: "L3" },
  { type: "verify", phase: "Phase 3 · Meter still working", label: "Meter re-check · known live source" },
];
const LSEQ120: RigStep[] = [
  { type: "verify", phase: "Phase 1 · Meter integrity", label: "Meter check · known live source" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "Line → Neutral", a: "L1", b: "N" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "Line → Ground", a: "L1", b: "G" },
  { type: "walk", phase: "Phase 2 · Walk the pairs", label: "Neutral → Ground", a: "N", b: "G" },
  { type: "verify", phase: "Phase 3 · Meter still working", label: "Meter re-check · known live source" },
];
export interface Term {
  x: number;
  y: number;
  label: string;
  sub: string;
  kind: "walk" | "ground";
}
const LTERM3: Record<string, Term> = {
  G: { x: 45, y: 185, label: "GND", sub: "Ground", kind: "ground" },
  L1: { x: 115, y: 185, label: "L1", sub: "Line 1", kind: "walk" },
  L2: { x: 185, y: 185, label: "L2", sub: "Line 2", kind: "walk" },
  L3: { x: 255, y: 185, label: "L3", sub: "Line 3", kind: "walk" },
};
const LTERM120: Record<string, Term> = {
  G: { x: 60, y: 185, label: "GND", sub: "Ground", kind: "ground" },
  L1: { x: 150, y: 185, label: "L", sub: "Line", kind: "walk" },
  N: { x: 240, y: 185, label: "N", sub: "Neutral", kind: "walk" },
};
export const LVERIFY = { a: { x: 105, y: 166 }, b: { x: 195, y: 166 } };
export const LMA = { x: 130, y: 124 };
export const LMB = { x: 172, y: 124 };

export function rigSeq(a: JspAsset): RigStep[] {
  return ldlStepsOf(a).length === 8 ? LSEQ3 : LSEQ120;
}
export function rigTerms(a: JspAsset): Record<string, Term> {
  return ldlStepsOf(a).length === 8 ? LTERM3 : LTERM120;
}
/** Where the two probes sit for a step. */
export function probesFor(a: JspAsset, step: RigStep): { a: Term; b: Term } {
  const tp = rigTerms(a);
  if (step.type === "verify") return { a: { ...LVERIFY.a, label: "", sub: "", kind: "walk" }, b: { ...LVERIFY.b, label: "", sub: "", kind: "walk" } };
  if (step.b === "G") return { a: tp.G!, b: tp[step.a!]! };
  return { a: tp[step.a!]!, b: tp[step.b!]! };
}
export function leadPath(m: { x: number; y: number }, p: { x: number; y: number }): string {
  const y = p.y - 24;
  return `M${m.x},${m.y} C${m.x},${m.y + 40} ${p.x},${y - 30} ${p.x},${y}`;
}
export function rigDots(a: JspAsset, at: number): string {
  return rigSeq(a)
    .map((s, i) => {
      let c = "nj-ldl-pdot";
      if (i < at) c += s.type === "verify" ? " done-verify" : " done-walk";
      else if (i === at) c += s.type === "verify" ? " current-verify" : " current-walk";
      return `<div class="${c}"></div>`;
    })
    .join("");
}

function ldlRig(a: JspAsset, s: JspState): string {
  const sq = rigSeq(a);
  if (s.ldl >= sq.length) {
    return '<div class="nj-ldl-done"><div class="nj-ldl-done-tag">SEQUENCE REHEARSED</div><div class="nj-ldl-done-check">✓</div><div style="font-size:14px;font-weight:800;margin-bottom:4px;">Sequence rehearsed</div><div style="font-size:11px;color:#94a3b8;line-height:1.5;max-width:250px;margin:0 auto;">This was the rehearsal. Prove the Zero Energy State for real at the equipment, in this order, after you submit. Step 7 is set.</div></div>';
  }
  const step = s.ldl;
  const cur = sq[step]!;
  const isV = cur.type === "verify";
  const t = probesFor(a, cur);
  const pa = leadPath(LMA, t.a);
  const pb = leadPath(LMB, t.b);
  const th = isV ? " hidden" : "";
  const sh = isV ? "" : " hidden";
  const lbl = isV ? (step === 0 ? "Confirm Meter Check" : "Confirm Meter Re-check") : "Trace This Path";
  const three = sq.length === 8;
  const ticks = [-56, -28, 0, 28, 56]
    .map((deg) => {
      const r = (deg * Math.PI) / 180;
      return `<line x1="${150 + Math.sin(r) * 16}" y1="${93 - Math.cos(r) * 16}" x2="${150 + Math.sin(r) * 20}" y2="${93 - Math.cos(r) * 20}" stroke="#F5C300" stroke-width="1.1"/>`;
    })
    .join("");
  const term = (w: "A" | "B", p: Term) =>
    `<g id="njLdlTerm${w}" class="nj-ldl-terminal${th}"><circle id="njLdlTerm${w}Fill" class="nj-ldl-term-fill ${p.kind}" cx="${p.x}" cy="${p.y}" r="26"/><circle id="njLdlTerm${w}Ring" class="nj-ldl-term-ring ${p.kind}" cx="${p.x}" cy="${p.y}" r="26"/><text id="njLdlTerm${w}Label" class="nj-ldl-term-label ${p.kind}" x="${p.x}" y="${p.y + 4}">${p.label}</text><text id="njLdlTerm${w}Sub" class="nj-ldl-term-sublbl" x="${p.x}" y="${p.y + 40}">${p.sub}</text></g>`;
  const probe = (id: string, p: Term, red: boolean) =>
    `<g class="nj-ldl-probe-tip" id="${id}" transform="translate(${p.x},${p.y})"><circle cx="0" cy="-24" r="1.5" fill="${red ? "#ef4444" : "#64748b"}"/><rect x="-4" y="-24" width="8" height="16" rx="1.5" fill="${red ? "#dc2626" : "#1e293b"}" stroke="${red ? "#7f1d1d" : "#0f172a"}" stroke-width=".8"/><rect x="-3" y="-22" width="1.5" height="12" fill="${red ? "#fca5a5" : "#475569"}" opacity=".7"/><rect x="-3" y="-8" width="6" height="3" fill="#cbd5e1" stroke="#64748b" stroke-width=".4"/><polygon points="-2,-5 2,-5 0,2" fill="#e2e8f0" stroke="#94a3b8" stroke-width=".4"/></g>`;
  return `<div class="nj-ldl-wrap"><div class="nj-ldl-tag">MENTAL REHEARSAL</div><div class="nj-ldl-headline">LDL Sequence</div>
    <div class="nj-ldl-pills"><div class="nj-ldl-pill">System ${a.volts === null ? "voltage not on record" : `${a.volts}V AC`}</div><div class="nj-ldl-pill">${three ? "Three-phase" : "Single-phase, line to neutral"}</div><div class="nj-ldl-pill">${sq.length} steps</div></div>
    <div id="njLdlPhase" class="nj-ldl-phase ${isV ? "verify" : "walk"}">${cur.phase}</div><div id="njLdlStepNum" class="nj-ldl-stepnum">Step ${step + 1} of ${sq.length}</div><div id="njLdlPairName" class="nj-ldl-pairname ${isV ? "verify" : "walk"}">${cur.label}</div>
    <div class="nj-ldl-stage"><svg viewBox="0 0 300 250" preserveAspectRatio="xMidYMid meet"><g>
      <rect x="108" y="2" width="84" height="132" rx="13" fill="#F5C300" stroke="#111" stroke-width="1.4"/><rect x="108" y="2" width="84" height="10" rx="5" fill="rgba(0,0,0,.08)"/><circle cx="184" cy="12" r="2.4" fill="#ef4444"/><text x="184" y="20" font-size="3.6" font-weight="800" text-anchor="middle" fill="#111">ALERT</text>
      <rect x="116" y="11" width="68" height="30" rx="4" fill="#0a1220" stroke="#111" stroke-width=".8"/><text id="njLdlMeterReading" class="${isV ? "meter-reading verify" : "meter-reading"}" x="150" y="30" style="font-size:13px;">${isV ? "220" : "000"}</text><text class="nj-ldl-meter-unit" x="150" y="38">V AC</text><text class="nj-ldl-meter-brand" fill="#111" x="150" y="50">FLUKE 117</text>
      <rect x="116" y="55" width="19" height="10" rx="2.5" fill="#2b2f36"/><text x="125.5" y="62" font-size="3.2" font-weight="800" text-anchor="middle" fill="#cbd5e1">HOLD</text><rect x="137" y="55" width="19" height="10" rx="2.5" fill="#2b2f36"/><text x="146.5" y="62" font-size="2.9" font-weight="800" text-anchor="middle" fill="#cbd5e1">MIN/MAX</text><rect x="158" y="55" width="15" height="10" rx="2.5" fill="#2b2f36"/><text x="165.5" y="62" font-size="2.9" font-weight="800" text-anchor="middle" fill="#cbd5e1">RANGE</text><rect x="175" y="55" width="9" height="10" rx="2.5" fill="#f59e0b"/>
      <circle cx="150" cy="93" r="20" fill="#1c1f24" stroke="#000" stroke-width="1.6"/><circle cx="150" cy="93" r="20" fill="none" stroke="#3a3f47" stroke-width=".8"/>${ticks}<line x1="150" y1="93" x2="141" y2="104" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/><circle cx="150" cy="93" r="3.4" fill="#eee" stroke="#111" stroke-width=".8"/>
      <circle class="nj-ldl-meter-port" cx="130" cy="124" r="4" fill="#111"/><text class="nj-ldl-meter-port-lbl" x="130" y="137">COM</text><circle class="nj-ldl-meter-port" cx="172" cy="124" r="4" fill="#ef4444"/><text class="nj-ldl-meter-port-lbl" x="172" y="137">V/Ω</text></g>
      <g id="njLdlSourceGroup" class="nj-ldl-terminal${sh}"><rect class="nj-ldl-source-panel" x="20" y="165" width="260" height="65" rx="8"/><text class="nj-ldl-source-label" x="150" y="185">Fluke PRV240 proving unit</text><text class="nj-ldl-source-sublabel" x="150" y="200">Known reference source</text><text class="nj-ldl-source-hint" x="150" y="215">Reading should show ~220V AC</text></g>
      ${term("A", t.a)}${term("B", t.b)}
      <path class="nj-ldl-lead-halo" id="njLdlHaloA" d="${pa}"/><path class="nj-ldl-lead-halo" id="njLdlHaloB" d="${pb}"/><path class="nj-ldl-lead-black" id="njLdlLeadA" d="${pa}"/><path class="nj-ldl-lead-red" id="njLdlLeadB" d="${pb}"/>
      ${probe("njLdlProbeA", t.a, false)}${probe("njLdlProbeB", t.b, true)}
    </svg></div><div class="nj-ldl-legend"><span><span class="sw" style="background:#64748b;"></span>BLACK · COM (reference)</span><span><span class="sw" style="background:#ef4444;"></span>RED · V/Ω (test point)</span></div>
    <button type="button" id="njLdlTraceBtn" class="nj-ldl-trace-btn${isV ? " verify" : ""}" data-j="ldladv">${lbl}</button><div id="njLdlDots" class="nj-ldl-dots">${rigDots(a, step)}</div></div>`;
}

// ---- The nodes ----

type Card = (v: JspView, n: number, tot: number, vd: Verdict) => string;

const CARD: Record<string, Card> = {
  Job: (v, n, tot) => {
    const t = v.t;
    return `<div class="card"><div class="nn">Node ${n} of ${tot}</div><div class="tierb t${t.tier}">${esc(TIER_LABEL[t.tier])}</div>
      <div class="tt" style="margin-bottom:4px">Job</div><div class="taskn">${esc(t.name)}<span class="pk">Picked at the asset</span></div>
      <div class="briefh"><span>Job Brief</span></div><textarea id="jbrief" aria-label="Job brief" placeholder="${esc(t.briefExample || "What are you planning to do?")}">${esc(v.s.brief)}</textarea>
      ${promptBlock(v, "Before You Start", "start")}
      ${
        !v.s.brief.trim()
          ? off("Add A Job Brief To Continue")
          : !promptsDone(t, v.s, "start")
            ? off("Answer Before You Start To Continue")
            : escalPending(t, v.s, "start")
              ? off("Answer The Notice Above To Continue")
              : gold("Continue", "next")
      }</div>`;
  },
  "Fit For Duty": (v, n, tot) => {
    const s = v.s;
    let h = head(n, tot, "Fit for duty");
    h += '<div class="q2">Are you focused, alert, and fit for duty? Free from fatigue, distraction, or impairment that could affect your safety on this task?</div>';
    h += `<div class="opts"><button type="button" class="o" data-jfit="YES" aria-pressed="${s.fit === "YES"}">Yes</button><button type="button" class="o" data-jfit="NO" aria-pressed="${s.fit === "NO"}">No</button></div>`;
    if (s.fit === "NO") h += '<div class="stopm">Step back from the equipment. Do not perform energized work while impaired or fatigued.</div>';
    return `${h}${s.fit === "YES" ? gold("Confirm To Advance", "next") : off("Answer Required To Continue")}</div>`;
  },
  "Need Power?": (v, n, tot) => {
    const s = v.s;
    const j = v.t.justify ?? { mode: "eswc", line: "" };
    let h = head(n, tot, "Does it need to be energized?");
    if (j.mode === "switching") {
      h += `<div class="note bl"><b>Switching task</b>${esc(j.line)}</div>`;
      h += cf(s, "loadOff", "Load interrupted with normal controls before the throw", "Disconnects are not load-break devices. NFPA 70E 120.5(2).");
      return `${h}${s.c.loadOff ? gold("Continue", "next") : off("Confirm To Continue")}</div>`;
    }
    if (j.mode === "inherent") {
      h += `<div class="note bl"><b>Inherently energized</b>${esc(j.line)}</div>`;
      h += '<div class="q2">The equipment must be energized and loaded for a valid scan. Confirm to continue.</div>';
      h += cf(s, "irLoad", "Equipment is energized and at ≥40% of rated load", "Under 40% the scan isn’t valid. Load % is recorded in Do The Work.");
      return `${h}${s.c.irLoad ? gold("Continue", "next") : off("Confirm To Continue")}</div>`;
    }
    if (j.line) h += `<div class="act"><div class="d">${esc(j.line)}</div></div>`;
    h += promptBlock(v, "First, confirm the test", "need");
    if (!promptsDone(v.t, s, "need") || escalPending(v.t, s, "need")) return `${h}${off("Answer The Question Above To Continue")}</div>`;
    if (scopeDeenPrompt(v.t, s)?.at === "need") return `${h}<div class="wr ok" style="margin-top:10px">De-energized steps added to this JSP after Risk.</div>${gold("Continue", "next")}</div>`;
    h += '<div class="q2">Can you do this with the equipment de-energized?</div><div class="opts">';
    h += `<button type="button" class="o" data-jplan="dead" aria-pressed="${s.plan === "dead"}">Yes, De-energize</button><button type="button" class="o" data-jplan="live" aria-pressed="${s.plan === "live"}">No, It Needs Power</button></div>`;
    if (s.plan === "dead") return `${h}<div class="wr ok" style="margin-top:10px">De-energized steps added to this JSP after Risk.</div>${gold("Continue", "next")}</div>`;
    if (s.plan === "live") {
      h += '<div class="q2" style="margin-top:12px">Why does it need power?</div><div class="olist">';
      for (const r of NEED_POWER_REASONS) h += `<button type="button" class="o" data-jpw="${r}" aria-pressed="${s.planWhy === r}">${r}</button>`;
      return `${h}</div>${s.planWhy ? gold("Continue", "next") : off("Pick A Reason")}</div>`;
    }
    return `${h}${off("Answer To Continue")}</div>`;
  },
  "Field Assessment": (v, n, tot) => {
    const s = v.s;
    const c = s.cond;
    let h = head(n, tot, "Equipment Field Assessment");
    h += '<div class="q2">Any smell, noise beyond normal hum, heat, or arcing right now?</div><div class="hlp">Check before you get closer.</div>' + yn(s, "sense");
    if (c.sense) {
      h += '<div class="opts" style="margin-top:6px">';
      for (const x of ["Smell", "Noise", "Heat", "Arcing"]) h += `<button type="button" class="o" data-jsn="${x}" aria-pressed="${c.senses.includes(x)}">${x}</button>`;
      h += "</div>";
      if (c.senses.length) h += `<div class="recd">Recorded: ${esc(c.senses.join(", ").toLowerCase())}.<span>Don’t open or operate this equipment energized. You acknowledge the risk on the next screen.</span></div>`;
    }
    h += `<div class="blk"><div class="q2">Doors closed and latched?</div>${yn(s, "doors")}`;
    h += `<div class="q2" style="margin-top:12px">Covers and deadfront in place?</div>${yn(s, "covers")}`;
    h += `<div class="q2" style="margin-top:12px">Any damage, burn marks, or discoloration?</div>${yn(s, "damage")}`;
    h += `<div class="q2" style="margin-top:12px">Dust, moisture, or debris on or near live parts?</div>${yn(s, "dust")}</div>`;
    h += '<div class="blk"><div class="q2">Tamper sticker intact?</div><div class="opts">';
    for (const [k, l] of [
      ["intact", "Intact"],
      ["broken", "Broken"],
      ["none", "None On This Equipment"],
    ] as const)
      h += `<button type="button" class="o" data-jtp="${k}" aria-pressed="${c.tamper === k}">${l}</button>`;
    h += "</div></div>";
    if (v.t.taskType === "Expose") {
      const nq = nearQuestion(v.a);
      h += `<div class="blk"><div class="q2">${esc(nq.q)}</div>${nq.hlp ? `<div class="hlp">${esc(nq.hlp)}</div>` : ""}${yn(s, "near")}`;
      h += `<div class="q2" style="margin-top:12px">Will the work expose line-side conductors?</div><div class="hlp">Opening the breaker does not kill its line side.</div>${yn(s, "line")}</div>`;
    }
    if (condDone(v.t, s) && !findings(s).length) h += '<div class="wr ok" style="margin-top:12px">Normal operating condition.</div>';
    return `${h}${condDone(v.t, s) ? gold("See Risk", "next") : off("Answer Every Question To See Risk")}</div>`;
  },
  Risk: (v, n, tot, vd) => {
    const s = v.s;
    const a = v.a;
    const dead = isDead(v.t, s);
    let h = head(n, tot, "Recognize the risk");
    h += `<div class="tiles">${tile("Shock", vd.shockLevel)}${tile("Arc flash", vd.arcLevel)}</div>`;
    if (!(dead && !s.deen)) h += `<div class="act"><div class="d">${esc(vd.headline)}</div></div>`;
    const pend = a.incidentEnergy === "pending";
    h += `<dl class="rec"><dt>Voltage</dt><dd class="${a.volts === null ? "pend" : ""}">${a.volts === null ? "Not on record" : `${a.volts} V`}</dd>
      <dt>Incident energy</dt><dd class="${pend ? "pend" : ""}">${esc(ieText(a))}</dd>
      <dt>Arc flash boundary</dt><dd class="${pend ? "pend" : ""}">${esc(a.arcFlashBoundary || "Unknown")}</dd>
      <dt>Serviceability</dt><dd>${esc(a.serviceability)}</dd>
      <dt>Overduty</dt><dd class="${a.overdutyThisDevice ? "warn" : ""}">${a.overdutyThisDevice ? "This device" : "No"}</dd>
      <dt>Upstream</dt><dd>${a.upstreamWalked ? "Verified" : "Not verified"}</dd>`;
    if (s.cond.tamper === "broken") h += '<div class="note2">Tamper sticker broken. IE basis less certain.</div>';
    h += "</dl>";
    h += `<button type="button" class="why" data-j="why" aria-expanded="${s.whyOpen}"><span>How It Got There</span><span class="cv">${s.whyOpen ? "Hide" : "Show"}</span></button>`;
    if (s.whyOpen) {
      h += `<div class="whyl"><b class="wh">What to do</b>${vd.actionLines.map((l) => `<div class="wl ${l.kind === "unknown" ? "unk" : l.kind === "raisedByYou" ? "you" : ""}">${esc(l.text)}</div>`).join("")}<b class="wh">Why</b><ol>${vd.causes.map((w) => `<li>${esc(w)}</li>`).join("")}</ol></div>`;
    }
    const locked = dead || vd.topLevel === "DANGER" || vd.topLevel === "PENDING";
    if (!locked) {
      h += '<div class="raise">';
      if (!s.raising) h += '<button type="button" class="rbtn" data-j="raise">Raise The Risk</button><div class="hl">You see something the record doesn’t. Raise it. Never lower.</div>';
      else {
        h += '<div class="q2">Which hazard?</div><div class="opts">';
        for (const [k, l] of [
          ["shock", "Shock"],
          ["arc", "Arc Flash"],
          ["both", "Both"],
        ] as const)
          h += `<button type="button" class="o" data-jhz="${k}" aria-pressed="${s.hz === k}">${l}</button>`;
        h += "</div>";
        if (s.hz) {
          const base = s.hz === "shock" ? LEVEL_RANK[vd.shockLevel] : s.hz === "arc" ? LEVEL_RANK[vd.arcLevel] : Math.min(LEVEL_RANK[vd.shockLevel], LEVEL_RANK[vd.arcLevel]);
          h += '<div class="q2" style="margin-top:10px">To what level?</div><div class="opts">';
          for (const l of ["MEDIUM", "HIGH", "DANGER"] as const) if (LEVEL_RANK[l] > base) h += `<button type="button" class="o lvl-${l}" data-jlv="${l}" aria-pressed="${s.lv === l}">${l}</button>`;
          h += "</div>";
        }
        if (s.lv) {
          h += '<div class="q2" style="margin-top:10px">Why?</div><div class="olist">';
          for (const r of RAISE_REASONS) h += `<button type="button" class="o" data-jrs="${r}" aria-pressed="${s.rs === r}">${r}</button>`;
          h += "</div>";
        }
        h += `<div class="row2"><button type="button" class="rbtn" data-j="raisecancel">Cancel</button>${
          s.hz && s.lv && s.rs ? '<button type="button" class="gbtn" data-j="raiseapply" style="margin-top:0;min-height:44px">Raise</button>' : '<button type="button" class="gbtn off" disabled style="margin-top:0;min-height:44px">Raise</button>'
        }</div>`;
      }
      h += "</div>";
    }
    if (!s.whyRead) h += off("Open How It Got There To Continue");
    else if (!dead && (vd.topLevel === "DANGER" || vd.topLevel === "PENDING")) h += `<button type="button" class="gbtn dng" data-j="ackdeen">${esc(vd.riskButton)}</button>`;
    else if (s.raising) h += off("Finish Or Cancel The Raise");
    else h += gold("Acknowledge The Risk", "next");
    return `${h}${ewLink(v, "Risk")}</div>`;
  },
  "Isolation Plan": (v, n, tot, vd) => {
    const { t, s } = v;
    let h = head(n, tot, "Plan the isolation");
    // Robert 2026-10-09: the isolation point box is removed from this node. It added confusion, not value.
    h += sec("Electrical sources");
    h += '<div class="q2">How many electrical sources feed this equipment?</div><div class="hlp">Count normal, backup, UPS, generator, and control power. Check the one-line, the labels, or trace the conductors.</div>';
    if (t.sources) h += `<div class="hlp"><b>This task:</b> ${esc(t.sources)}</div>`;
    h += '<div class="opts">';
    for (const c of [1, 2, 3, 4] as const) h += `<button type="button" class="o" data-jsrc="${c}" aria-pressed="${s.srcCount === c}">${c === 4 ? "4+" : c}</button>`;
    h += '</div><div style="height:8px"></div>';
    h += cf(s, "srcAll", "Every source identified. I know every source that can energize this equipment.", "Multi-feed, tapped, or shared-neutral conditions checked.");
    if (t.tier === 3) h += promptBlock(v, "This task’s sources");
    if (asksWorkArea(t)) {
      h += sec("Your work area");
      h += `<div class="q2">${esc(WORK_AREA_QUESTION)}</div><div class="hlp">Test Dead proves the device. It does not prove the parts around your hands.</div>`;
      h += `<div class="opts"><button type="button" class="o" data-jla="0" aria-pressed="${s.liveRemain === false}">No</button><button type="button" class="o" data-jla="1" aria-pressed="${s.liveRemain === true}">Yes</button></div>`;
      if (s.liveRemain) h += `<div class="wr" style="margin-top:8px">${esc(LIVE_PARTS_LINE)}</div>`;
    }
    h += sec("Other hazardous energy");
    h += '<div class="q2">Other hazardous energy on this equipment?</div><div class="hlp">Other energy is controlled under its own procedure. Recognize it here. You confirm it is controlled at Lock Out. Mechanical covers rotating parts, gravity and springs.</div><div class="egrid">';
    for (const o of ENERGY_OPTIONS) h += `<button type="button" class="eo${s.energy[o] ? " on" : ""}" data-jeg="${o}">${esc(o)}</button>`;
    h += `</div><button type="button" class="eo none${s.energyNone ? " on" : ""}" data-j="enone">None Apply. Electrical Only</button>`;
    h += sec("Temporary grounding (A7)");
    h += '<div class="q2">Could this equipment be backfed after isolation? Generator, on-site generation, or another source.</div><div class="hlp">If yes, apply temporary protective grounds (120.5(7)). Most tasks won’t need this. It’s here so you don’t miss the rare one that does.</div>';
    h += `<div class="opts"><button type="button" class="o" data-jgr="1" aria-pressed="${s.grounding === true}">Required</button><button type="button" class="o" data-jgr="0" aria-pressed="${s.grounding === false}">Not Required</button></div>`;
    if (s.grounding) h += '<div class="note bl" style="margin-top:10px">Grounding armed. Required in the isolation checklist ahead.</div>';
    const ok = s.srcCount !== null && s.c.srcAll && energyOk(s) && s.grounding !== null && (!asksWorkArea(t) || s.liveRemain !== null) && promptsDone(t, s) && !escalPending(t, s);
    return `${h}${ok ? gold("Isolation Planned. Proceed", "next") : off(escalPending(t, s) ? "Acknowledge The Notice Above To Continue" : "Complete The Plan To Continue")}</div>`;
  },
  "PPE And Boundary": (v, n, tot, vd) => {
    const { t, a, s } = v;
    const keys: string[] = [];
    let h: string;
    const lab = `LAB ${esc(a.limitedApproach || "Unknown")}${ragText(a)} · AF boundary ${esc(a.arcFlashBoundary || "Unknown")}. ${esc(ESCORT_LINE)}`;
    // Decision 68 condition lines on Expose tasks (SH-11, SH-12).
    const exposeLines = t.taskType === "Expose" ? EXPOSE_LINES.map((x) => `<div class="bul or">${esc(x)}</div>`).join("") : "";
    if (isDead(t, s)) {
      const live = liveRemains(t, s);
      const offLine = `PPE may be reduced only AFTER a successful LDL${s.grounding ? " and after your grounds are on" : ""}.`;
      h = head(n, tot, "Prepare to cross the boundary", live ? "The equipment is live until LDL. Full arc-rated PPE goes on before you cross." : "The equipment is live until LDL. Full arc-rated PPE goes on before you cross. You open the door and run LDL in protection.");
      h += sec("Establish the boundary");
      h += cf(s, "boundary", "Approach boundaries marked. Unqualified persons excluded.", lab);
      h += exposeLines;
      h += `<div class="blkh">PPE <span>${live ? "For the whole job" : "For switching and LDL"}</span></div>${ppeBox(a)}${posterRow(s)}`;
      if (live) h += `<div class="wr" style="margin-top:8px">${esc(LIVE_PARTS_LINE)}</div>`;
      h += cf(s, "ppeDonned", a.incidentEnergy === "pending" ? `${ppeLevel(a)} donned` : "Full arc-rated PPE donned per incident energy values", live ? "" : esc(offLine));
      h += gloves(s);
      const ok = all(s, ["boundary", "poster", "ppeDonned"]) && glovesAnswered(s);
      return `${h}${ok ? gold("Prepared. Begin Isolation", "next") : off("Complete The Prep To Continue")}</div>`;
    }
    if (t.tier === 1) {
      h = head(n, tot, "Work practices and PPE", esc(t.name));
      h += '<div class="blkh">A · Work Practices</div>';
      h += promptBlock(v, "Confirm your situation");
      for (const c of t.controls) h += `<div class="bul">${esc(c)}</div>`;
      if (t.door) h += `<div class="bul or">${esc(t.door)}</div>`;
      if (t.doorBranch) {
        h += `<div class="q2" style="margin-top:10px">${esc(t.doorBranch.q)}</div><div class="opts"><button type="button" class="o" data-jdoor="open" aria-pressed="${s.door === "open"}">Yes</button><button type="button" class="o" data-jdoor="closed" aria-pressed="${s.door === "closed"}">No</button></div>`;
        if (s.door === "open") {
          h += '<div class="note bl" style="margin-top:8px">No contact with exposed parts. Doesn’t escalate. Establish the approach boundary for others.</div>';
          h += `${sec("Why the door’s open")}<div class="olist">`;
          for (const r of t.doorBranch.reasons) h += `<button type="button" class="o" data-jrsn="${esc(r.v)}" aria-pressed="${s.reason === r.v}">${esc(r.label)}</button>`;
          h += "</div>";
          if (s.reason === "broken") h += '<div class="wr ok" style="margin-top:8px">Logged for repair in the Work Request Log. Reset from the inside and continue.</div>';
          h += sec("Before you reach in") + cf(s, "affirm", esc(t.doorBranch.affirm));
          h += cf(s, "lab", `Limited approach boundary established and marked · ${a.volts ? `${a.volts}V` : "voltage not on record"}`, `Unqualified persons kept clear. Alert anyone nearby. ${esc(ESCORT_LINE)}`);
          keys.push("affirm", "lab");
        }
      }
      h += '<div style="height:8px"></div>';
      if (t.encl && s.cond.doors === true && s.cond.covers === true) {
        h += cf(s, "encl", esc(t.encl));
        keys.push("encl");
      }
      h += cf(s, "area", "Work area clear", "Egress, lighting, housekeeping. No unexpected hazards.");
      keys.push("area");
      const plane = doorPlane(t, s) && LEVEL_RANK[vd.arcLevel] <= 1;
      if (t.rabLine) h += `<div class="note bl" style="margin-top:8px">${esc(RAB_LINE)}</div>`;
      h += `<div class="blkh">B · PPE <span>From Risk: shock ${vd.shockLevel} · arc ${vd.arcLevel}</span></div>`;
      h += ppeBox(a, plane) + posterRow(s);
      keys.push("poster");
      // Decision 62 item 4: at the door plane, hands outside the RAB, no arc-rated PPE at any IE.
      if (LEVEL_RANK[vd.arcLevel] >= 2 || (a.incidentEnergy === "pending" && !plane)) {
        h += cf(s, "ppeDonned", `${ppeLevel(a)} on`);
        keys.push("ppeDonned");
      }
      const needGloves = tier1Gloves(a, t, s);
      if (needGloves) h += gloves(s);
      if (s.cond.covers === false) {
        h += cf(s, "barricade", "Barricade up. Keep others out.", "Covers or deadfront missing at the field assessment.");
        keys.push("barricade");
      }
      const can = all(s, keys) && (!needGloves || glovesAnswered(s)) && promptsDone(t, s) && !escalPending(t, s) && (!t.doorBranch || s.door !== null) && (s.door !== "open" || s.reason !== null);
      return `${h}${can ? gold("Confirm To Advance", "next") : off(escalPending(t, s) ? "Acknowledge The Notice Above To Continue" : "Confirm Each Step To Continue")}</div>`;
    }
    const ir = t.ppeMode === "ir";
    h = head(n, tot, ir ? "Prepare · position and protection" : "Prepare to cross the boundary", ir ? "PPE depends on whether a cover comes off. Stand back, or protect up." : "PPE on before you cross. The QEW crosses in with protection.");
    h += `<div class="blkh">A · Boundary</div><div class="txt">${esc(t.boundary || "Mark the boundary. Keep unqualified persons out. Standby stays outside the arc flash boundary.")}</div>`;
    h += cf(s, "boundary", "Approach boundaries marked. Unqualified persons excluded.", lab);
    keys.push("boundary");
    if (t.rabLine) h += `<div class="note bl" style="margin-top:8px">${esc(RAB_LINE)}</div>`;
    h += exposeLines;
    let needGloves = false;
    let irOpen = false;
    if (ir) {
      h += `${sec("Cover removal. Does it change the PPE?")}<div class="q2">Does the scan require removing a bolted / non-hinged panel with a tool?</div>`;
      h += `<div class="opts"><button type="button" class="o" data-jirc="no" aria-pressed="${s.irCover === "no"}">No, Stand Back</button><button type="button" class="o" data-jirc="yes" aria-pressed="${s.irCover === "yes"}">Yes, Tool And Panel</button></div>`;
      if (s.irCover === "no") {
        h += `<div class="wr ok" style="margin-top:8px">No covers off and you stay back: arc-rated PPE and gloves are not required for the scan. Cross the line and they are.</div>${cf(s, "irStand", `Standing ≥${esc(a.limitedApproach || "the LAB")} from exposed conductors. No covers removed.`, "Scan through existing openings / hinged doors only. Cross that line and PPE is required.")}`;
        keys.push("irStand");
      } else if (s.irCover === "yes") {
        h += `<div class="stopm" style="margin-top:8px">Removing a bolted panel exposes you to the hazard. Wear arc-rated PPE and shock protection for this asset.</div>${ewLink(v, "PPE And Boundary")}${ppeBox(a)}${posterRow(s)}${cf(s, "ppeDonned", "Arc-rated PPE + shock protection donned per incident energy values")}${gloves(s)}`;
        keys.push("poster", "ppeDonned");
        needGloves = true;
      } else irOpen = true;
    } else {
      h += `<div class="blkh">PPE <span>From Risk: shock ${vd.shockLevel} · arc ${vd.arcLevel}</span></div>${ppeBox(a)}${posterRow(s)}`;
      h += cf(s, "ppeDonned", a.incidentEnergy === "pending" ? `${ppeLevel(a)} donned` : "Full arc-rated PPE donned per incident energy values");
      h += gloves(s);
      keys.push("poster", "ppeDonned");
      needGloves = true;
    }
    h += `${sec("Standby / awareness")}<div class="q2">Is a second QEW available on the job as standby?</div>`;
    h += `<div class="opts"><button type="button" class="o" data-jsb="standby" aria-pressed="${s.standbyMode === "standby"}">Yes</button><button type="button" class="o" data-jsb="alone" aria-pressed="${s.standbyMode === "alone"}">Working Alone</button></div>`;
    if (s.standbyMode === "standby") {
      h += `<div style="height:8px"></div>${cf(s, "standby", esc(t.standby || "Second QEW stands by outside the arc flash boundary"), "One works, one observes. Voice contact maintained throughout.")}`;
      keys.push("standby");
    } else if (s.standbyMode === "alone") {
      // LOTO_WORDS.md rev 2: the Work Started email at Submit tells the site contacts. No separate notify.
      h += '<div class="wr" style="margin-top:8px">No second QEW on site. When you submit, the Work Started email tells the site contacts you are working alone.</div>';
    }
    const aware = (s.standbyMode === "standby" && s.c.standby) || s.standbyMode === "alone";
    const can = all(s, keys) && !irOpen && (!needGloves || glovesAnswered(s)) && aware;
    // Diagnostic Cover Removal switched on: the cover comes off only under an approved EEWP.
    if (coverNeedsPermit(t, s, v.ew!.ctx, v.ew!.permit)) return `${h}${off("Approved EEWP Required To Continue")}</div>`;
    return `${h}${can ? gold("Prep Complete. Continue", "next") : off("Complete The Prep To Continue")}</div>`;
  },
  "Lock Out": (v, n, tot, vd) => {
    const { t, s } = v;
    const cnt = sourceCountText(s.srcCount);
    const many = s.srcCount !== 1;
    let h = head(n, tot, "Isolate And Lock Out All Respective Energy Sources");
    h += `<div class="iso"><div class="h">From the Isolation Plan</div><div class="d2">Lock all ${esc(cnt)} source${many ? "s" : ""}</div><div class="n">Isolation point: ${esc(vd.isolationPoint.kind)}</div></div>`;
    const steps: Record<(typeof LOCK_STEPS)[number], [string, string]> = {
      notify: ["Notify affected personnel before interrupting load", ""],
      interruptLoad: [
        "Interrupt load and open all disconnects, including alternate feeds",
        [t.load ? `${t.load}${t.backfeed ? `. ${t.backfeed}` : ""}` : "", BINDING_HANDLE_LINE].filter(Boolean).join(" "),
      ],
      bladesVerified: ["Verify blade position visually, through the window or by direct observation. Don’t trust the handle.", ""],
      storedReleased: ["Release stored electrical energy", t.stored || "Verify ZERO on every terminal before you touch it."],
      lotoApplied: [`Apply LOTO on all ${cnt} source${many ? "s" : ""} per the equipment-specific documented procedure`, ""],
      tryStart: ["Operate the isolating device(s) to confirm the equipment cannot restart", ""],
    };
    const row = (key: string, label: string, det: string) => {
      const on = Boolean(s.s2[key]);
      return `<button type="button" class="chk${on ? " on" : ""}" data-js2="${key}"><span class="bx">${on ? "✓" : ""}</span><span><b>${label}</b>${det ? `<span class="det">${esc(det)}</span>` : ""}</span></button>`;
    };
    h += sec("Checklist · electrical isolation");
    LOCK_STEPS.forEach((k, i) => {
      h += row(k, `${i + 1}. ${esc(steps[k][0])}`, steps[k][1]);
      // NFPA 70E 120.5: stored electrical, then stored non-electrical, then the locks.
      if (k === "storedReleased")
        for (const o of energyList(s)) {
          const on = s.energyConfirm[o] === "confirmed";
          h += `<button type="button" class="chk${on ? " on" : ""}" data-jec="${esc(o)}"><span class="bx">${on ? "✓" : ""}</span><span><b>${esc(o)}</b><span class="det">Released or blocked, verified zero under its own procedure.</span></span></button>`;
        }
    });
    return `${h}${lockOutDone(s) ? gold("Isolated And Locked. Prove It Dead", "next") : off("Complete Isolation To Continue")}${ewLink(v, "Lock Out")}${closeBlock(s)}</div>`;
  },
  "Test Dead": (v, n, tot) => {
    const { a, s } = v;
    let h = head(n, tot, "Prove the Zero Energy State");
    const done = s.ldl >= rigSeq(a).length;
    h += ldlRig(a, s);
    h += `<div class="chk${done ? " on" : " dim"}"><span class="bx">${done ? "✓" : "□"}</span><span><b>7. Zero Energy State. Rehearsed here, proven at the equipment</b>${done ? "" : '<span class="det2">Walk the sequence above to set this.</span>'}</span></div>`;
    if (done) {
      if (liveRemains(v.t, s)) h += `<div class="wr" style="margin-top:8px">${esc(LIVE_PARTS_LINE)}</div>`;
      const g = s.grounding === true;
      if (g)
        h += `<button type="button" class="chk${s.s2.grounds ? " on" : ""}" data-js2="grounds"><span class="bx">${s.s2.grounds ? "✓" : ""}</span><span><b>8. Apply temporary protective grounds where required</b><span class="det">Answered Required in the Isolation Plan. Backfeed risk identified. Apply grounds, then confirm here.</span></span></button>`;
      else h += '<div class="chk on dim"><span class="bx">✓</span><span><b>8. Apply temporary protective grounds where required</b><span class="det2">Answered Not Required in the Isolation Plan.</span></span></div>';
      return `${h}${!g || s.s2.grounds ? gold("Continue To Sign Off", "next") : off("Confirm Grounds To Continue")}${closeBlock(s)}</div>`;
    }
    return `${h}${closeBlock(s)}</div>`;
  },
  "Do The Work": (v, n, tot) => {
    const { t, s } = v;
    let h = head(n, tot, `${t.doVerb} · perform the task`, esc(t.name));
    if (t.peak) h += `<div class="peak"><b>Peak-risk moment</b>${esc(t.peak)}</div>`;
    h += promptBlock(v, "Confirm your situation");
    const keys: string[] = [];
    if (t.meterVerify) {
      // JSP cleanup PR 1, A5: one tick, one line. The box and the Technique repeat came off.
      h += cf(s, "meterVerified", "Meter verified", "CAT III/IV for the voltage. Leads, tips, guards and fuses inspected. Reads on a known live source. Fails to read, stop.");
      keys.push("meterVerified");
    }
    if (t.technique.length) h += `<div class="tech"><b>Technique · how you do it here</b>${t.technique.map((c) => `<div class="bul gr">${esc(c)}</div>`).join("")}</div>`;
    const can = all(s, keys) && promptsDone(t, s) && !escalPending(t, s);
    return `${h}${can ? gold("Continue", "next") : off(escalPending(t, s) ? "Acknowledge The Notice Above To Continue" : promptsDone(t, s) ? "Verify The Meter To Continue" : "Answer The Question Above To Continue")}${ewLink(v, "Do The Work")}</div>`;
  },
  "Sign Off": (v, n, tot, vd) => {
    const { t, s } = v;
    const dead = isDead(t, s);
    const rows: [string, string][] = [
      ["Task", t.name],
      ["Risk", `Shock ${vd.shockLevel} · Arc ${vd.arcLevel}${s.raised ? " · raised by you" : ""}`],
    ];
    if (dead) {
      const sp = scopeDeenPrompt(t, s);
      const where = sp ? (sp.at === "start" ? "Job" : sp.at === "need" ? "Need Power?" : "Do The Work") : null;
      rows.push(["Basis", `ESWC per NFPA 70E 120.5${where ? `. Chosen at ${where}` : s.plan === "dead" ? ". Chosen at Need Power?" : s.deen ? ". DANGER at Risk" : ""}`]);
      rows.push(["Sources", `${sourceCountText(s.srcCount)} electrical source${s.srcCount === 1 ? "" : "s"} identified in the Isolation Plan${energyList(s).length ? `, plus ${energyList(s).join(", ")} (other energy)` : ""}`]);
      rows.push(["Isolate at", `${vd.isolationPoint.kind}. Found in the field`]);
      rows.push(["LDL", "Zero-energy state rehearsed · prove dead at the equipment"]);
      if (s.grounding) rows.push(["Grounds", "Required. Apply, then remove after the work"]);
      if (asksWorkArea(t)) rows.push(["Work area", liveRemains(t, s) ? "Live parts remain. PPE on for the whole job" : "Nothing in the work area stays energized"]);
    } else if (t.tier === 1) {
      if (s.door === "open") {
        rows.push(["Door", `Open. ${s.reason === "broken" ? "Reset defect logged" : "By design"}`]);
        rows.push(["Boundary", "Limited approach established. Others excluded"]);
      }
    } else {
      rows.push([
        "Basis",
        s.eewpApproved && t.tier === 3
          ? `Energized under ${permitIdOf(v.jspNumber)}`
          : t.justify?.mode === "inherent"
            ? "Inherently energized. ≥40% load"
            : t.justify?.mode === "switching"
              ? "Switching. Load interrupted before the throw"
              : `Needs power. ${s.planWhy ?? ""}`,
      ]);
      rows.push(["PPE", t.ppeMode === "ir" && s.irCover === "no" ? "Stand-back scan · no covers removed · no PPE" : "Arc-rated per IE values · Class 0 gloves · poster reviewed"]);
      rows.push(["Coverage", s.standbyMode === "alone" ? "Lone QEW. Personnel notified" : "Second QEW standby outside AF boundary"]);
      if (t.meterVerify) rows.push(["Meter", "Verified on known source"]);
    }
    // Work requests have their own session (JSP_WRITES_WORDS.md section 3). The findings stay on the JSP record.
    if (vd.followUp.includes("Work request") || findings(s).length) rows.push(["Findings", "Recorded on this JSP"]);
    if (s.eewpApproved && v.ew?.permit) {
      const by = v.ew.permit.approvals.filter((x) => x.decision === "approved").map((x) => x.name).join(", ");
      rows.push(["EEWP", `${v.ew.permit.permitId} approved by ${by}. PPE confirmed`]);
    }
    let h = head(n, tot, "Submit the plan");
    h += sec("Job summary") + rows.map((r) => `<div class="srow"><span>${r[0]}</span><span>${esc(r[1])}</span></div>`).join("");
    if (v.error) h += `<div class="stopm">${esc(v.error)}</div>`;
    if (dead) {
      h += '<div class="planst">This is my isolation plan. I will lock out, prove it dead at the equipment, and keep my lock on until the work is done.</div>';
      h += v.busy ? '<button type="button" class="gbtn dark" disabled>Submitting…</button>' : '<button type="button" class="gbtn dark" data-j="submit">Confirm The Plan. Submit JSP</button>';
    } else h += v.busy ? '<button type="button" class="gbtn dark" disabled>Submitting…</button>' : '<button type="button" class="gbtn dark" data-j="submit">Submit JSP. Begin Work</button>';
    h += '<div class="subnote">Submitting emails the site contacts that this work has started.</div>';
    return `${h}</div>`;
  },
};

// ---- EEWP screens (MOCKUP_EEWP.html rev 2b) ----

function ewHead(label: string, title: string, sub = ""): string {
  return `<div class="card"><div class="nn">${label}</div><div class="tt">${esc(title)}</div>${sub ? `<div class="tsub">${sub}</div>` : ""}`;
}

/**
 * One step back in the EEWP request (NAV_AUDIT.md item 2, Robert 2026-10-22). Before, only a browser Back did
 * this, and the Home Screen app has none. On the first step it leaves the request and returns to the JSP node.
 */
function ewBack(v: JspView): string {
  if (v.ew!.busy) return "";
  const first = v.ew!.flow!.step === "ask1";
  return `<div class="ewlinkrow"><button type="button" class="ewl" data-j="ewback">${first ? "Back To The JSP" : "Back"}</button></div>`;
}

function ewPaths(v: JspView): string {
  const n = v.ew!.ctx?.required ?? 2;
  return `<div class="ewpaths"><div class="ewp dead"><b>De-Energized</b><span>Lock out. Test dead. Work.</span><span>Your lock, your control.</span></div><div class="ewp live"><b>Energized</b><span>Second QEW on the job.</span><span>Full arc flash PPE.</span><span>A permit and approvals.</span><span>${esc(waitingOn(n))}</span></div></div>`;
}

function ewArea(id: string, value: string, placeholder: string, label: string): string {
  return `<textarea id="${id}" class="ewta" maxlength="2000" aria-label="${esc(label)}" placeholder="${esc(placeholder)}">${esc(value)}</textarea>`;
}

/** The QEW's half: the seven questions plus the optional photo. */
export function ewFormProblem(v: JspView): string | null {
  return v.ew?.flow ? eewpAnswersProblem(eewpAnswersFrom(v.ew.flow.answers), v.ew.qews.map((q) => q.badgeId)) : null;
}

function ewCard(v: JspView, vd: Verdict): string {
  const f = v.ew!.flow!;
  const err = v.ew!.error ? `<div class="stopm">${esc(v.ew!.error)}</div>` : "";
  if (f.step === "ask1") {
    let h = ewHead("EEWP · Before You Ask · 1 of 2", "Can This Be De-Energized?", "Most jobs end here. That is the point.");
    h += '<div class="q2">NFPA 70E accepts only two reasons to work it live:</div>';
    h += NFPA_REASONS.map(([b, sub]) => `<div class="ewreason"><b>${esc(b)}</b><span>${esc(sub)}</span></div>`).join("");
    h += `<div class="stopm" style="margin-top:6px">${esc(NOT_REASONS)}</div>${ewPaths(v)}`;
    h += gold("Go De-Energized", "ewdead");
    return `${h}<div class="ewlinkrow"><button type="button" class="ewl" data-j="ewask2">No, It Must Stay Energized</button></div>${ewBack(v)}</div>`;
  }
  if (f.step === "ask2") {
    let h = ewHead("EEWP · Before You Ask · 2 of 2", "Can It Wait For A Planned Outage?");
    h += '<div class="q2">A planned outage turns this into a normal de-energized job.</div>';
    h += v.ew!.busy ? off("Writing The Work Request…") : gold("Yes, Request An Outage", "ewoutage");
    h += '<div class="hlp" style="margin-top:6px">Writes a work request for the outage. This JSP stops.</div>';
    return `${h}${err}<div class="ewlinkrow"><button type="button" class="ewl" data-j="ewform">No, It Cannot Wait</button></div>${ewBack(v)}</div>`;
  }
  const a = f.answers;
  if (f.step === "form") {
    const pressed = (on: boolean) => `aria-pressed="${on}"`;
    let h = ewHead("EEWP · Your Half", "Answer These Before You Ask");
    h += '<div class="hlp2">Your answers go word for word to the approvers and the ESP Manager.</div>';
    h += `<div class="q2">1. What exactly has to be done live?</div>${ewArea("ewq1", a.liveWork, f.from === "coverRemoval" ? "Remove and refit the covers for the scan" : "The whole job, or one step", "What has to be done live")}`;
    h += '<div class="q2">2. Why can’t it be de-energized?</div><div class="olist">';
    for (const [k, l] of JUSTIFY_OPTIONS) h += `<button type="button" class="o" data-jewc="${k}" ${pressed(a.justificationCategories.includes(k))}>${l}</button>`;
    h += `</div>${ewArea("ewq2", a.justification, "The specifics, in your words", "Why it can’t be de-energized")}`;
    h += `<div class="q2">3. What happens if it is de-energized?</div>${ewArea("ewq3", a.ifDeEnergized, "One line", "What happens if it is de-energized")}`;
    h += `<div class="q2">4. Can part of the job be done dead?</div><div class="opts"><button type="button" class="o" data-jewpd="yes" ${pressed(a.partDead === "yes")}>Yes</button><button type="button" class="o" data-jewpd="no" ${pressed(a.partDead === "no")}>No</button></div>`;
    if (a.partDead === "yes") h += `<div style="height:8px"></div>${ewArea("ewq4", a.partDeadWhat, "Which part, done dead first", "Which part can be done dead")}`;
    h += '<div class="q2" style="margin-top:12px">5. Second qualified person</div>';
    if (!v.ew!.qewsLoaded) h += '<div class="hlp">Loading the Active QEWs at this site…</div>';
    else if (!v.ew!.qews.length) {
      // Rev 3 decision 5: no other Active QEW at the site. A standby person, never nobody.
      h = h.replace('<div class="q2" style="margin-top:12px">5. Second qualified person</div>', '<div class="q2" style="margin-top:12px">5. Standby Person</div>');
      h += '<div class="wr">No other Active QEW at this site. Someone must stand by. Never alone on energized work.</div>';
      h += `<div class="q2">Name</div><input id="ewsbn" class="ewsel" type="text" maxlength="80" aria-label="Standby person's name" value="${esc(a.standbyName)}">`;
      h += `<div class="q2" style="margin-top:8px">Job title</div><input id="ewsbt" class="ewsel" type="text" maxlength="80" aria-label="Standby person's job title" value="${esc(a.standbyTitle)}">`;
      const first = a.standbyName.trim().split(" ")[0] ?? "";
      h += `<div class="q2" style="margin-top:10px">${first ? `Hand your phone to ${esc(first)}. ${esc(first)} confirms:` : "Hand your phone to the standby person. They confirm:"}</div>`;
      for (const [k, l] of STANDBY_TICKS) {
        const on = a.standbyTicks.includes(k);
        h += `<button type="button" class="cf${on ? " on" : ""}" data-jewsb="${k}"><span class="bx" aria-hidden="true">${on ? "✓" : ""}</span><span><b>${esc(l)}</b></span></button>`;
      }
      h += `<div class="hlp">The approvers see: No second QEW at this site. Standby: ${esc(a.standbyName || "name")}, ${esc(a.standbyTitle || "job title")}.</div>`;
    } else {
      h += `<select id="ewq5" class="ewsel" aria-label="Second qualified person"><option value=""${a.secondQewId ? "" : " selected"}>Pick a QEW</option>${v.ew!.qews
        .map((q) => `<option value="${esc(q.badgeId)}"${a.secondQewId === q.badgeId ? " selected" : ""}>${esc(q.name)} · ${esc(q.badgeId)}</option>`)
        .join("")}</select>`;
      const pick = v.ew!.qews.find((q) => q.badgeId === a.secondQewId);
      h += `<div class="hlp" style="margin-top:4px">${pick ? esc(pick.name.split(" ")[0] ?? pick.name) : "They"} confirm${pick ? "s" : ""} on their own phone. That is agreement, not approval.</div>`;
    }
    h += `<div class="q2" style="margin-top:12px">6. If something goes wrong</div>${ewArea("ewq6", a.emergencyPlan, "Who calls for help, the nearest upstream disconnect, the way out", "If something goes wrong")}`;
    h += '<div class="q2">Photo of the work area <span class="opt">Optional</span></div>';
    h += `<label class="ewphoto">${f.photo ? `<img src="${f.photo}" alt="Photo of the work area">Change Photo` : "＋ Add Photo"}<input id="ewphoto" type="file" accept="image/*" capture="environment" aria-label="Photo of the work area"></label>`;
    h += `<div class="q2" style="margin-top:12px">7. Can this work be done safely?</div><div class="opts"><button type="button" class="o" data-jews="yes" ${pressed(a.safeToProceed === "yes")}>Yes</button><button type="button" class="o" data-jews="no" ${pressed(a.safeToProceed === "no")}>No</button></div>`;
    if (a.safeToProceed === "no") return `${h}<div class="stopm">${esc(SAFE_NO)}</div>${gold("Go De-Energized", "ewdead")}${ewBack(v)}</div>`;
    h += '<div class="hlp" style="margin-top:4px">No ends the request. Yes is your signature.</div>';
    const problem = ewFormProblem(v);
    return `${h}${err}${problem ? off("Answer Every Question To Continue") : '<button type="button" class="gbtn dark" data-j="ewreview">Continue</button>'}${ewBack(v)}</div>`;
  }
  // review: the JSP's half, frozen at Submit For Approval.
  const num = v.jspNumber.replace("JSP-", "");
  const scope = scopeOf(f.from);
  const rows = jspHalfOf({
    a: v.a,
    t: v.t,
    s: v.s,
    jspNumber: v.jspNumber,
    location: v.ew!.location,
    requesterName: "",
    requesterId: "",
    scope,
    shockLevel: vd.shockLevel,
    arcLevel: vd.arcLevel,
    todayShort: v.ew!.todayShort,
  }).filter((r) => r.label !== "Requester");
  let h = ewHead("EEWP · From Your JSP", "What The Approvers Will See");
  h += `<div class="hlp2">Carried over from JSP #${esc(num)}. Read only. Frozen when you submit.</div>`;
  let sec_ = "";
  for (const r of rows) {
    if (r.section !== sec_) {
      sec_ = r.section;
      h += sec(esc(sec_));
    }
    h += `<div class="srow"><span>${esc(r.label)}</span><span>${esc(r.value)}</span></div>`;
  }
  const n = v.ew!.ctx?.required ?? 2;
  h += err;
  h += v.ew!.busy ? '<button type="button" class="gbtn dark" disabled>Sending…</button>' : '<button type="button" class="gbtn dark" data-j="ewsubmit">Submit For Approval</button>';
  return `${h}<div class="hlp" style="margin-top:6px;text-align:center">Sends ${esc(permitIdOf(v.jspNumber))} to ${n} ${n === 1 ? "approver" : "approvers"} at ${esc(v.a.siteName)}. Expires in 12 hours.</div>${ewBack(v)}</div>`;
}

/** The JSP waits. No work starts. */
function ewWaiting(v: JspView): string {
  const p = v.ew!.permit!;
  const got = approvedCount(p);
  let h = `<div class="ewwait"><b>Waiting On EEWP · ${esc(p.permitId)}</b><span>${got} of ${p.approvalsRequired} approved. ${esc(untilLine(p.approvalsRequired))}</span>`;
  for (const x of p.approvals) {
    const ok = x.decision === "approved";
    // Rev 3 decision 3: Opened or Not opened yet, so the QEW knows when to pick up the phone.
    const state = ok ? `approved ${x.at ? clockOf(x.at) : ""}`.trim() : x.openedAt ? `Opened ${clockOf(x.openedAt)}` : "Not opened yet";
    h += `<div class="ewap"><span class="${ok ? "ok" : "wt"}" aria-hidden="true">${ok ? "✓" : "…"}</span>${esc([x.name, x.role, state].filter(Boolean).join(" · "))}</div>`;
  }
  const sq = p.secondQew;
  if (sq.name) {
    const line = sq.kind === "standby" ? `${sq.name} · Standby · agreed in person` : sq.confirmedAt ? `${sq.name} agreed · Second QEW` : `${sq.name} · Second QEW · waiting`;
    h += `<div class="ewap"><span class="${sq.confirmedAt ? "ok" : "wt"}" aria-hidden="true">${sq.confirmedAt ? "✓" : "…"}</span>${esc(line)}</div>`;
  }
  if (p.expiresAt) h += `<span class="exp">Expires ${esc(clockOf(p.expiresAt))}</span>`;
  h += '<span class="ewupd">Updates by itself. You get one email when it is decided.</span>';
  if (v.ew!.error) h += `<div class="stopm">${esc(v.ew!.error)}</div>`;
  h += v.ew!.busy ? '<button type="button" class="rbtn sm" disabled>Withdrawing…</button>' : '<button type="button" class="rbtn sm" data-j="ewwithdraw">Withdraw</button>';
  return `${h}${ewPrintLink(v)}</div>`;
}

function ewPrintLink(v: JspView): string {
  return v.ew!.print ? '<div class="ewlinkrow" style="margin-top:4px"><button type="button" class="ewlk" data-j="ewprint">Print Permit</button></div>' : "";
}

/** Approved: Confirm PPE And Begin. Each item ticked, then the JSP opens under the permit. */
function ewApproved(v: JspView): string {
  const p = v.ew!.permit!;
  const by = p.approvals.filter((x) => x.decision === "approved").map((x) => x.name);
  const names = by.length <= 2 ? by.join(" and ") : `${by.slice(0, -1).join(", ")} and ${by[by.length - 1]}`;
  let h = `<div class="card"><div class="nn">${esc(p.permitId)} · Approved</div><div class="tt">Confirm PPE And Begin</div>`;
  h += `<div class="ewok">Approved by ${esc(names)}. Valid for JSP #${esc(v.jspNumber.replace("JSP-", ""))} only.${p.scope === "coverRemoval" ? " Cover removal and refit only." : ""}</div>`;
  p.ppeItems.forEach((item, i) => {
    const on = Boolean(v.ew!.ppeTicks[String(i)]);
    h += `<button type="button" class="cf${on ? " on" : ""}" data-jewppe="${i}"><span class="bx" aria-hidden="true">${on ? "✓" : ""}</span><span><b>${esc(item)}</b></span></button>`;
  });
  if (v.ew!.error) h += `<div class="stopm">${esc(v.ew!.error)}</div>`;
  const all = p.ppeItems.every((_, i) => v.ew!.ppeTicks[String(i)]);
  h += v.ew!.busy ? off("Confirming…") : all ? '<button type="button" class="gbtn dark" data-j="ewppe">Confirm PPE And Begin</button>' : off("Tick Each Item To Begin");
  return `${h}${ewPrintLink(v)}</div>`;
}

function closeArea(v: JspView): string {
  const c = v.close;
  const dead = isDead(v.t, v.s);
  const num = v.jspNumber.replace("JSP-", "");
  if (c.done) return `<div class="cbanner"><b>${c.autoAt ? `JSP #${esc(num)} complete.` : `JSP #${esc(num)} closed.`}</b><span>Thanks. The record is complete.</span><button type="button" class="gbtn dark" data-j="exit" style="margin-top:10px">Back To The QEW Page</button></div>`;
  // Item 5: after the app closed it, the grey banner asks for the answer (MOCKUP_JSP_APPROVED.html, JSP_CLOSE_NET_WORDS.md).
  let h = c.autoAt
    ? `<div class="cbanner auto"><b>Closed by the app at ${esc(c.autoAt)}.</b><span>As-left not reported. Tell us how you left it.</span>`
    : '<div class="cbanner"><b>Work in progress.</b><span>Close this JSP when the work is done.</span>';
  if (!c.open) return `${h}<button type="button" class="gbtn dark" data-j="openclose" style="margin-top:10px">${c.autoAt ? "Tell Us How You Left It" : "Close JSP"}</button></div>`;
  h += '</div><div class="card cpill"><div class="tt">Close The JSP</div>';
  h += `<div class="q2">Is the work done?</div><div class="opts"><button type="button" class="o" data-jcw="done" aria-pressed="${c.work === "done"}">Done</button><button type="button" class="o" data-jcw="stopped" aria-pressed="${c.work === "stopped"}">Stopped, Not Finished</button></div>`;
  h += `<div class="q2" style="margin-top:12px">How did you leave the equipment?</div><div class="olist"><button type="button" class="o" data-jleft="same" aria-pressed="${c.leftAs === "same"}">As I Found It</button>`;
  if (findings(v.s).length) h += `<button type="button" class="o" data-jleft="fixed" aria-pressed="${c.leftAs === "fixed"}">Fixed What I Found</button>`;
  h += `<button type="button" class="o" data-jleft="worse" aria-pressed="${c.leftAs === "worse"}">${LEFT_AS_LABEL.worse}</button></div>`;
  if (c.leftAs === "worse") h += `<textarea id="jworse" aria-label="${REPAIR_NOTE_PROMPT}" placeholder="${REPAIR_NOTE_PROMPT} Optional" style="margin-top:8px">${esc(c.note)}</textarea>`;
  if (dead)
    h += `<div class="q2" style="margin-top:12px">Where did you leave the isolation?</div><div class="opts"><button type="button" class="o" data-jrts="rts" aria-pressed="${c.isolation === "rts"}">Returned To Service</button><button type="button" class="o" data-jrts="locked" aria-pressed="${c.isolation === "locked"}">Left Locked Out</button></div>`;
  const reNeeded = dead && c.isolation === "rts";
  if (reNeeded) {
    h += '<div class="q2" style="margin-top:12px">Before you re-energize</div>';
    for (const [k, label] of REENERGIZE_CHECKS) {
      const on = Boolean(c.reEnergize[k]);
      h += `<button type="button" class="cf${on ? " on" : ""}" data-jre="${k}"><span class="bx" aria-hidden="true">${on ? "✓" : ""}</span><span><b>${label}</b></span></button>`;
    }
  }
  if (c.error) h += `<div class="stopm">${esc(c.error)}</div>`;
  const ok = c.work && c.leftAs && (!dead || c.isolation) && (!reNeeded || REENERGIZE_CHECKS.every(([k]) => c.reEnergize[k]));
  const label = c.autoAt ? "Send" : "Close JSP";
  return `${h}${c.busy ? off(c.autoAt ? "Sending…" : "Closing…") : ok ? gold(label, "closefinal") : off(c.autoAt ? "Answer To Send" : "Answer To Close")}</div>`;
}

/** The whole JSP page: yellow header, black bar with pills and the SOP button, asset line, rail. */
export function jspHtml(v0: JspView): string {
  const v: JspView = { ...v0, ew: v0.ew ?? noEewpView() };
  const { a, t, s } = v;
  const vd = verdictOf(a, t, s);
  const n = railOf(t, s);
  let ai = n.indexOf(s.phase);
  if (ai < 0) ai = n.length - 1;
  if (s.submitted) ai = n.length;
  const num = v.jspNumber.replace("JSP-", "");
  // Back To The QEW Page before Submit (QEW Page Return Path, approved 2026-10-09). The JSP stays a draft.
  // After Submit too (NAV_AUDIT.md item 1, Robert 2026-10-22): the Home Screen app has no Back, so the JSP
  // must never trap him mid-work. The JSP stays open; the asset page shows Close JSP #n to come back to.
  // No Cancel after Submit: a submitted JSP is closed, never cancelled.
  const back = s.submitted
    ? v.close.done
      ? ""
      : '<button type="button" class="backq" data-j="exit">Back To The QEW Page</button>'
    : '<button type="button" class="backq" data-j="backqew">Back To The QEW Page</button><button type="button" class="cancq" data-j="cancelask">Cancel</button>';
  let h = `<div class="jh"><div class="y"><b>Job Safety Plan<small>JSP #${esc(num)}</small></b><div class="yr">${back}</div></div><div class="k">${v.pills
    .map((p) => `<div class="apill ${p.tone}">${esc(p.label)}</div>`)
    .join("")}${t.sopNumber ? `<button type="button" class="sopb" data-j="sop">${esc(t.sopNumber)} · Open</button>` : ""}</div></div>`;
  h += `<div class="asset"><div class="id">${esc(a.assetId)}</div><div class="nm">${esc([a.equipmentType, a.area, a.siteName, a.volts ? `${a.volts} V` : "voltage not on record"].filter(Boolean).join(" · "))}</div></div>`;
  if (!s.submitted && v.cancel?.ask) {
    const c = cancelConfirmText(v.jspNumber, v.cancel.taskName || t.name, v.cancel.startedTime);
    h += `<div class="cancbox"><b>${esc(c.title)}</b><span>${esc(c.body)}</span>${v.cancel.error ? `<div class="stopm">${esc(v.cancel.error)}</div>` : ""}<div class="row2">${
      v.cancel.busy
        ? '<button type="button" class="gbtn dng" disabled style="margin-top:0;min-height:44px">Cancelling…</button>'
        : '<button type="button" class="rbtn" data-j="cancelkeep">Keep Working</button><button type="button" class="gbtn dng" data-j="cancelgo" style="margin-top:0;min-height:44px">Cancel JSP</button>'
    }</div></div>`;
  }
  h += '<div class="rail">';
  n.forEach((k, i) => {
    const d = i < ai;
    const ac = i === ai;
    const last = i === n.length - 1;
    const isNew = isDead(t, s) && t.tier !== 3 && ["Isolation Plan", "Lock Out", "Test Dead"].includes(k);
    h += `<div class="row"><div class="rc"><div class="dot ${d ? "done" : ac ? "act" : "lock"}">${d ? "✓" : i + 1}</div>${last ? "" : `<div class="ln${d ? " done" : ""}"></div>`}</div><div class="rb">`;
    if (d) {
      const dng = k === "Risk" && s.deen ? " dng" : "";
      h += s.submitted
        ? `<div class="pill${dng}"><span class="l">${k}</span><span class="s">${esc(doneLine(a, t, s, k))}</span></div>`
        : `<button type="button" class="pill ed${dng}" data-jgo="${esc(k)}"><span class="l">${k}</span><span class="s">${esc(doneLine(a, t, s, k))}</span><span class="e">Edit</span></button>`;
    } else if (ac) {
      // EEWP: the permit waits, or the request screens open, in place of the node (MOCKUP_EEWP.html rev 2b).
      const ew = v.ew!;
      const pm = ew.permit;
      if (ew.checking) h += '<div class="card"><div class="txt">Checking the EEWP…</div></div>';
      else if (pm?.status === "Waiting") h += ewWaiting(v);
      else if (pm?.status === "Approved" && !pm.ppeConfirmedAt) h += ewApproved(v);
      else if (ew.flow) h += ewCard(v, vd);
      else {
        // Denied or expired: back to the JSP with the reason. Go de-energized or discard.
        if (pm && (pm.status === "Denied" || pm.status === "Expired") && !s.submitted)
          h += `<div class="stopm" style="margin:5px 0">${esc(pm.permitId)} ${pm.status === "Denied" ? "denied" : "expired"}. ${esc(pm.endReason)} Go de-energized, or discard this JSP.</div>`;
        h += CARD[k]!(v, i + 1, n.length, vd);
      }
    }
    else h += `<div class="pill lk"><span class="l">${k}${isNew ? '<span class="newtag">NEW</span>' : ""}</span><span class="s">${esc(tc(NODE_SUB[k] ?? ""))}</span></div>`;
    h += "</div></div>";
  });
  if (s.submitted) h += closeArea(v);
  return `<div class="jsp">${h}</div></div>`;
}

export function posterOverlay(): string {
  return '<div class="jsp"><div class="ov" role="dialog" aria-label="PPE poster"><div class="ovc wide"><div class="sh">PPE Requirements</div><div class="sb"><img src="/ppe-poster.jpg" alt="Shock and arc flash PPE requirements poster"><button type="button" class="gbtn" data-j="postok" style="margin-top:4px">I Checked The Poster</button><button type="button" class="rbtn" data-j="ovx">Back</button></div></div></div></div>';
}

export function noContentHtml(taskName: string): string {
  return `<div class="jsp"><div class="jh"><div class="y"><b>Job Safety Plan</b></div></div><div class="card" style="margin:12px"><div class="tt">${esc(taskName)}</div><div class="stopm">This task has no JSP content yet. See your ESP Manager.</div><button type="button" class="rbtn" data-j="exit" style="margin-top:10px">Back</button></div></div>`;
}
